/**
 * Documentation Hub Landing Page
 * Central entry point for all CalWMP resources and documentation
 */

import { Link } from 'react-router-dom';
import type { ReactElement } from 'react';

interface ResourceCardProps {
  title: string;
  description: string;
  link: string;
  icon: ReactElement;
  badge?: string;
  external?: boolean;
}

function ResourceCard({ title, description, link, icon, badge, external = false }: ResourceCardProps) {
  const cardContent = (
    <>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
            {badge && (
              <span className="px-2 py-0.5 text-xs font-medium bg-accent-100 text-accent-700 rounded">
                {badge}
              </span>
            )}
          </div>
          <p className="text-sm text-neutral-600 line-clamp-2">{description}</p>
        </div>
        <svg
          className="w-5 h-5 text-neutral-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </>
  );

  if (external) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="block card hover:shadow-lg transition-shadow"
      >
        {cardContent}
      </a>
    );
  }

  return (
    <Link to={link} className="block card hover:shadow-lg transition-shadow">
      {cardContent}
    </Link>
  );
}

interface SectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

function Section({ title, description, children }: SectionProps) {
  return (
    <section className="mb-12">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">{title}</h2>
        {description && <p className="text-neutral-600">{description}</p>}
      </div>
      {children}
    </section>
  );
}

export function DocsHub() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
          CalWMP Resources & Documentation
        </h1>
        <p className="text-xl text-neutral-600 mb-8 max-w-3xl mx-auto">
          Everything you need to create compliant waste management plans and meet CalGreen 5.408 requirements
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Link to="/plans/create" className="btn-primary">
            Create Your First Plan
          </Link>
          <Link to="/docs/calgreen-overview" className="btn-secondary">
            Learn About CalGreen
          </Link>
        </div>
      </div>

      {/* Popular Resources Highlight */}
      <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8 mb-12 border border-primary-100">
        <div className="flex items-center gap-3 mb-4">
          <svg
            className="w-6 h-6 text-primary-600"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
          <h2 className="text-2xl font-bold text-neutral-900">Most Helpful Resources</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ResourceCard
            title="Materials Handling Guide"
            description="Comprehensive guide to handling, separating, and diverting construction materials"
            link="/docs/materials-guide"
            badge="Comprehensive"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
          <ResourceCard
            title="Frequently Asked Questions"
            description="Quick answers to common questions about CalWMP and compliance"
            link="/docs/faq"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>
      </div>

      {/* Getting Started Section */}
      <Section
        title="Getting Started"
        description="New to CalWMP or CalGreen compliance? Start here."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ResourceCard
            title="Understanding CalGreen 5.408"
            description="Learn about California's waste diversion requirements and who must comply"
            link="/docs/calgreen-overview"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
          />
          <ResourceCard
            title="How to Create Your First Plan"
            description="Step-by-step walkthrough of the plan creation wizard"
            link="/plans/create"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            }
          />
          <ResourceCard
            title="Frequently Asked Questions"
            description="Quick answers to common questions about CalWMP and compliance"
            link="/docs/faq"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>
      </Section>

      {/* Planning Your Project Section */}
      <Section
        title="Planning Your Project"
        description="Tools and resources to help you create effective waste management plans."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ResourceCard
            title="Materials Handling Guide"
            description="Detailed guidance on recycling, reusing, and disposing of construction materials"
            link="/docs/materials-guide"
            badge="Recommended"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            }
          />
          <ResourceCard
            title="Example Plans"
            description="Real-world examples of compliant waste management plans"
            link="/docs/examples"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            }
          />
          <ResourceCard
            title="Glossary of Terms"
            description="Common terminology used in waste management and CalGreen compliance"
            link="/docs/glossary"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
          />
          <div className="card bg-neutral-50 border-neutral-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-neutral-200 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-neutral-700">Diversion Calculator</h3>
                  <span className="px-2 py-0.5 text-xs font-medium bg-neutral-300 text-neutral-700 rounded">
                    Coming Soon
                  </span>
                </div>
                <p className="text-sm text-neutral-500">
                  Estimate your project&apos;s waste quantities and diversion rates
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Legal & Compliance Section */}
      <Section
        title="Legal & Compliance"
        description="Important legal information and policies."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ResourceCard
            title="Disclaimer"
            description="Legal disclaimer and limitations of service"
            link="/disclaimer"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            }
          />
          <ResourceCard
            title="Terms of Service"
            description="User agreement and terms of use"
            link="/terms"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
          <ResourceCard
            title="Privacy Policy"
            description="How we collect and protect your data"
            link="/privacy"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            }
          />
          <ResourceCard
            title="Accessibility"
            description="Our commitment to accessibility"
            link="/accessibility"
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>
      </Section>

      {/* Support Section */}
      <Section
        title="Support"
        description="Get help when you need it."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ResourceCard
            title="Contact Support"
            description="Email our support team for assistance"
            link="mailto:support@calwmp.com"
            external
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
          />
          <ResourceCard
            title="GitHub Repository"
            description="View source code, report issues, or contribute"
            link="https://github.com/calwmp/calwmp"
            external
            icon={
              <svg className="w-6 h-6 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
            }
          />
          <div className="card bg-neutral-50 border-neutral-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-neutral-200 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-neutral-700">Community Forum</h3>
                  <span className="px-2 py-0.5 text-xs font-medium bg-neutral-300 text-neutral-700 rounded">
                    Coming Soon
                  </span>
                </div>
                <p className="text-sm text-neutral-500">
                  Connect with other users and share best practices
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* External Resources Section */}
      <Section
        title="External Resources"
        description="Official California resources for waste management and compliance."
      >
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <div>
                <a
                  href="https://www.dgs.ca.gov/BSC/Resources/Page-Content/Building-Standards-Commission-Resources-List-Folder/CALGreen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-900 font-semibold hover:text-blue-700 underline"
                >
                  Official CALGreen Standards
                </a>
                <p className="text-sm text-blue-800 mt-1">California Green Building Standards Code from the Building Standards Commission</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <div>
                <a
                  href="https://www.calrecycle.ca.gov/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-900 font-semibold hover:text-blue-700 underline"
                >
                  CalRecycle
                </a>
                <p className="text-sm text-blue-800 mt-1">California Department of Resources Recycling and Recovery</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <div>
                <a
                  href="https://www2.calrecycle.ca.gov/SWFacilities/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-900 font-semibold hover:text-blue-700 underline"
                >
                  Find Recycling Facilities
                </a>
                <p className="text-sm text-blue-800 mt-1">Search CalRecycle&apos;s database of waste and recycling facilities</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Footer Disclaimer */}
      <div className="mt-12 pt-6 border-t border-neutral-200">
        <p className="text-sm text-neutral-600 text-center italic">
          CalWMP is an independent service provider and is not affiliated with, endorsed by, or connected to
          the State of California or any government agency. Information is provided for educational purposes only.
          Always consult with your local building department for specific compliance requirements.
        </p>
      </div>
    </div>
  );
}